import { CalendarClock, Check, ClipboardCheck, Pencil, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatClockTime, formatDateOnly } from '../../../../lib/formatDate';
import { usePersonNames } from '../../personnel/usePersonNames';
import { applicantDisplayName } from '../applicantName';
import { applicantsApi } from '../applicantsApi';
import { APPLICANT_QUALIFICATION, type ApplicantListItem, type JobPostingListItem } from '../applicantTypes';
import { ApplicantInterviewModal } from './ApplicantInterviewModal';
import { ApplicantModal } from './ApplicantModal';
import { ApplicantScreeningModal } from './ApplicantScreeningModal';
import { usePermission } from '../../../../auth/AuthContext';
import { deliberationApi } from '../../deliberation/deliberationApi';
import {
  APPOINTED_FILTER,
  DELIBERATION_READ_PERMISSION,
  ENDORSED_FILTER,
  SHORTLISTED_FILTER,
} from '../../deliberation/deliberationTypes';
import { ApplicantFilterRail, type ApplicantFilterKey } from '../../deliberation/components/ApplicantFilterRail';

/**
 * A job posting's applicants — the Applicants tab of BOTH the posting's profile and the vacancy's
 * (19 Oct 2026). The tab is where the cycle's two applicant stages are recorded: **Initial
 * screening** and **Schedule interview** on each row, the second only for a Qualified applicant.
 *
 * Server-side search, sort and paging. **The NAME can be neither searched nor sorted**: it is in
 * the other database, so the placeholder names what the search reaches. Names are resolved ONE
 * batch per page, through the applicant-scoped door (`/api/applicants/names`), never through
 * `/api/persons/resolve` — reading applicants does not mean reading the 201 files.
 */
export function ApplicantsPanel({
  posting,
  onChanged,
}: {
  /** The posting (the tag) — its id, and whether it still takes applicants. */
  posting: Pick<JobPostingListItem, 'id' | 'isActive' | 'isPublished' | 'isCompleted'>;
  /** The posting's applicant count moved — the profile refetches its badge. */
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();

  const [showDeleted, setShowDeleted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ApplicantListItem | null>(null);
  const [screening, setScreening] = useState<ApplicantListItem | null>(null);
  const [interviewing, setInterviewing] = useState<ApplicantListItem | null>(null);
  /** One applicant from the row menu, or several from the selection bar — one dialog either way. */
  const [deleting, setDeleting] = useState<ApplicantListItem[] | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The FILTER RAIL (product call, 21 Oct 2026) — Deliberate's own, the same component: All · Short Listed ·
    Endorsed · Appointment. Every entry past All reads a DELIBERATE value, so the rail — and the calls
    behind it — are drawn only for a reader holding `deliberations.read`; anyone else sees the table alone.
  */
  const canDeliberate = usePermission(DELIBERATION_READ_PERMISSION);
  const [filter, setFilter] = useState<ApplicantFilterKey>('all');
  const [counts, setCounts] = useState<Partial<Record<'all' | 'shortlisted' | 'endorsed' | 'appointed', number>>>({});

  const fetchPage = useCallback(
    async (query: DataTableQuery, signal: AbortSignal) => {
      const shortlist =
        filter === 'shortlisted'
          ? SHORTLISTED_FILTER
          : filter === 'endorsed'
            ? ENDORSED_FILTER
            : filter === 'appointment'
              ? APPOINTED_FILTER
              : undefined;

      /*
        A Deliberate entry pages through Deliberate's list (the same Applicants search, sort and paging,
        narrowed server-side) and takes its applicant rows; All stays this module's own list, deleted rows
        and all. The counts ride on Deliberate's facet, against the same search.
      */
      const [page, deliberated] = await Promise.all([
        shortlist === undefined
          ? applicantsApi.list(
              posting.id,
              {
                search: query.search,
                page: query.page,
                pageSize: query.pageSize,
                sort: query.sort,
                includeDeleted: showDeleted,
              },
              signal,
            )
          : null,
        canDeliberate
          ? deliberationApi.listApplicants(
              posting.id,
              {
                search: query.search,
                page: shortlist === undefined ? 1 : query.page,
                pageSize: shortlist === undefined ? 1 : query.pageSize,
                sort: query.sort,
                shortlist,
              },
              signal,
            )
          : null,
      ]);

      if (deliberated)
        setCounts(Object.fromEntries((deliberated.facets?.shortlist ?? []).map((f) => [f.value, f.count])));

      return page
        ? { items: page.items, total: page.total }
        : { items: deliberated!.items.map((r) => r.applicant), total: deliberated!.total };
    },
    [posting.id, showDeleted, filter, canDeliberate],
  );

  // Unsorted means the server's order: most recently applied first.
  const state = useDataTable<ApplicantListItem>({ fetchPage, pageSize: 10 });

  const personIds = useMemo(() => state.rows.map((a) => a.personId), [state.rows]);
  const names = usePersonNames(personIds, applicantsApi.names);

  const refresh = useCallback(() => {
    state.reload();
    onChanged();
  }, [state, onChanged]);

  async function confirmDelete() {
    if (!deleting) return;

    setRunning(true);
    setError(null);

    let done = 0;

    try {
      // One request per applicant, so each lands in its own record's History Logs.
      for (const applicant of deleting) {
        await applicantsApi.remove(applicant.id);
        done += 1;
      }
      toast.success(deleting.length === 1 ? 'Applicant deleted.' : `${deleting.length} applicants deleted.`);
      setDeleting(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not delete the applicant.', 0));
      // A run stopped partway still deleted some; show what is now true.
      if (done > 0) refresh();
    } finally {
      setRunning(false);
    }
  }

  async function restore(applicant: ApplicantListItem) {
    try {
      // The undo takes no ceremony.
      await applicantsApi.restore(applicant.id);
      toast.success('Applicant restored.');
      refresh();
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not restore the applicant.');
    }
  }

  const columns: DataColumn<ApplicantListItem>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 260,
      // Not sortable: the name is in the other database.
      render: (a) => <TextCell value={applicantDisplayName(a.personId, names)} />,
    },
    {
      key: 'email',
      header: 'Email',
      width: 220,
      sortable: true,
      summary: true,
      render: (a) => <TextCell value={a.email} />,
    },
    {
      key: 'mobileNumber',
      header: 'Mobile number',
      width: 160,
      summary: true,
      render: (a) => <TextCell value={a.mobileNumber} mono />,
    },
    {
      key: 'dateApplied',
      header: 'Date applied',
      width: 140,
      sortable: true,
      summary: true,
      // A calendar date, not an instant.
      render: (a) => <TextCell value={formatDateOnly(a.dateApplied)} mono />,
    },
    {
      // Initial Screening's outcome — blank until screened.
      key: 'screeningResult',
      header: 'Screening',
      width: 130,
      render: (a) => <TextCell value={a.screeningResultLabel} />,
    },
    {
      // Initial Interview's schedule — one value, the day and the time of day.
      key: 'interviewDate',
      header: 'Interview',
      width: 190,
      render: (a) => (
        <TextCell
          value={
            a.interviewDate != null ? `${formatDateOnly(a.interviewDate)}, ${formatClockTime(a.interviewTime)}` : null
          }
          mono
        />
      ),
    },
    {
      key: 'interviewPlace',
      header: 'Interview place',
      width: 180,
      render: (a) => <TextCell value={a.interviewPlace} />,
    },
    {
      // The application's STAGE — a lifecycle, so it sits beside the record's status pill.
      key: 'stage',
      header: 'Application status',
      width: 160,
      sortable: true,
      stage: true,
      render: (a) => <StatusText tone="inactive">{a.statusLabel}</StatusText>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (a) =>
        a.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
    },
  ];

  const table = (
    <div className="flex h-full min-w-0 flex-1 flex-col p-4">
      <DataTablePanel<ApplicantListItem>
        id="applicants"
        state={state}
        columns={columns}
        rowKey={(a) => a.id}
        fill
        searchPlaceholder="Search email, mobile number or remarks"
        onCreate={() => setAdding(true)}
        createLabel="Add applicant"
        createPermission="applicants.create"
        createDisabledReason={
          !posting.isActive
            ? 'This posting has ended — it takes no new applicants. Its applicants are kept.'
            : !posting.isPublished
              ? 'This posting is not published yet. Publish it under Vacancies before recording applicants.'
              : posting.isCompleted
              ? "This posting is completed — an endorsed applicant's appointment is recorded. It takes no new applicants unless the endorsement is withdrawn."
              : null
        }
        onRowOpen={(a) => navigate(`/applicants/${posting.id}/${a.id}`)}
        openPermission="applicants.read"
        onDelete={(a) => setDeleting([a])}
        deletePermission="applicants.delete"
        deleteDisabledReason={(a) => (a.isActive ? null : 'This applicant is already deleted.')}
        bulkActions={(selected) => {
          const deletable = selected.filter((a) => a.isActive);

          return [
            {
              key: 'delete-applicants',
              label: `Delete ${deletable.length} applicant${deletable.length === 1 ? '' : 's'}`,
              permission: 'applicants.delete',
              destructive: true,
              disabledReason: deletable.length === 0 ? 'Every selected applicant is already deleted.' : null,
              onClick: () => setDeleting(deletable),
            },
          ];
        }}
        rowActions={(a) => [
          {
            // The cycle's second stage. A re-screen corrects it in place.
            key: `screen-${a.id}`,
            label: 'Initial screening',
            icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
            permission: 'applicants.update',
            disabledReason: a.isActive ? null : 'This applicant is deleted. Restore them before screening.',
            onClick: () => setScreening(a),
          },
          {
            // The third — for a Qualified applicant only; the server refuses anyone else.
            key: `interview-${a.id}`,
            label: a.interviewDate != null ? 'Reschedule interview' : 'Schedule interview',
            icon: <CalendarClock className="h-4 w-4" aria-hidden="true" />,
            permission: 'applicants.update',
            disabledReason: !a.isActive
              ? 'This applicant is deleted. Restore them before scheduling an interview.'
              : a.screeningResult !== APPLICANT_QUALIFICATION.Qualified
                ? 'Only an applicant screened Qualified is interviewed. Record the initial screening first.'
                : null,
            onClick: () => setInterviewing(a),
          },
          {
            // In the menu rather than `onEdit`, which carries no disabled reason — the References
            // panel's shape, for the same reason.
            key: `edit-${a.id}`,
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
            permission: 'applicants.update',
            disabledReason: a.isActive ? null : 'This applicant is deleted. Restore them before editing.',
            onClick: () => setEditing(a),
          },
          {
            key: `restore-${a.id}`,
            label: 'Restore',
            icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
            permission: 'applicants.delete',
            disabledReason: a.isActive ? 'This applicant is not deleted.' : null,
            onClick: () => void restore(a),
          },
        ]}
        otherInfo={(a) => a}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
            // Deliberate's entries list LIVE applicants only — a deleted one is not deliberated on.
            disabledReason:
              filter === 'all' ? null : 'Deleted applicants are listed under All only — they are not deliberated on.',
          },
        ]}
        emptyMessage={
          filter === 'shortlisted'
            ? 'No applicant is shortlisted yet.'
            : filter === 'endorsed'
              ? 'No applicant is endorsed for appointment yet.'
              : filter === 'appointment'
                ? 'No appointment recorded yet.'
                : posting.isActive
                ? 'No applicant recorded yet.'
                : 'This posting ended with no applicant recorded.'
        }
        emptySearchMessage="No applicant matches that search."
      />
    </div>
  );

  return (
    <div className="flex h-full min-h-0 min-w-0">
      {canDeliberate ? (
        <ApplicantFilterRail
          id="applicants-filter"
          active={filter}
          counts={counts}
          onSelect={(key) => {
            setFilter(key);
            // A narrower list re-reads from its first page.
            state.setPage(1);
          }}
        />
      ) : null}

      {table}

      {adding || editing !== null ? (
        <ApplicantModal
          postingId={posting.id}
          editing={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {screening ? (
        <ApplicantScreeningModal
          applicant={screening}
          applicantName={applicantDisplayName(screening.personId, names)}
          onClose={() => setScreening(null)}
          onSaved={() => {
            setScreening(null);
            refresh();
          }}
        />
      ) : null}

      {interviewing ? (
        <ApplicantInterviewModal
          applicant={interviewing}
          applicantName={applicantDisplayName(interviewing.personId, names)}
          onClose={() => setInterviewing(null)}
          onSaved={() => {
            setInterviewing(null);
            refresh();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setError(null);
          }}
          onConfirm={() => void confirmDelete()}
          title={deleting.length === 1 ? 'Delete applicant' : `Delete ${deleting.length} applicants`}
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="applicant-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {deleting.length === 1
                  ? `${applicantDisplayName(deleting[0].personId, names)} is taken off this posting's applicants.`
                  : `${deleting.length} applicants are taken off this posting's applicants.`}{' '}
                Nothing is erased — the records and the names are kept, and a deleted applicant can be restored from
                this list.
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
